/**
 * Admin (Structura): Arrepentimiento / Baja de servicio de la suscripción SaaS
 * del comercio con nosotros — NO confundir con sm_consumer_requests (eso es
 * de cada tenant hacia SUS propios clientes finales, vive en /app/cumplimiento).
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, ChevronLeft, ChevronRight, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { CrudEmptyState } from "@/components/ds";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import type {
  PlatformComplianceRequest,
  PlatformComplianceStatus,
  PlatformComplianceType,
} from "@/lib/db-service/platform-compliance-requests";

type ListResult = { items: PlatformComplianceRequest[]; total: number };

const STATUS_LABELS: Record<PlatformComplianceStatus, string> = {
  SOLICITADA: "Solicitada",
  RECIBIDA: "Recibida",
  EN_GESTION: "En gestión",
  RESUELTA: "Resuelta",
  RECHAZADA: "Rechazada",
};

const STATUS_COLORS: Record<PlatformComplianceStatus, string> = {
  SOLICITADA: "bg-yellow-100 text-yellow-800",
  RECIBIDA: "bg-blue-100 text-blue-800",
  EN_GESTION: "bg-purple-100 text-purple-800",
  RESUELTA: "bg-green-100 text-green-800",
  RECHAZADA: "bg-red-100 text-red-800",
};

const TYPE_LABELS: Record<PlatformComplianceType, string> = {
  ARREPENTIMIENTO: "Arrepentimiento",
  BAJA_SERVICIO: "Baja de servicio",
};

const STATUS_TRANSITIONS: Record<string, string[]> = {
  SOLICITADA: ["RECIBIDA", "EN_GESTION", "RECHAZADA"],
  RECIBIDA: ["EN_GESTION", "RESUELTA", "RECHAZADA"],
  EN_GESTION: ["RESUELTA", "RECHAZADA"],
};

const PAGE_SIZE = 20;

export function PlatformComplianceRequestsPanel() {
  const [items, setItems] = useState<PlatformComplianceRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<PlatformComplianceRequest | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (typeFilter !== "all") params.set("type", typeFilter);
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));
      const data = await apiGetJson<ListResult>(`/api/admin/compliance-requests?${params}`);
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, q]);

  useEffect(() => { void load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const today = new Date().toDateString();
  const todayCount = items.filter((i) => new Date(i.createdAt).toDateString() === today).length;
  const pendingCount = items.filter((i) => i.status !== "RESUELTA" && i.status !== "RECHAZADA").length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="rounded-lg border bg-muted/30 px-3 py-1.5 text-xs">
          <span className="font-semibold">{total}</span> total ·{" "}
          <span className="font-semibold">{pendingCount}</span> pendientes ·{" "}
          <span className="font-semibold">{todayCount}</span> hoy (en esta página)
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar por email, nombre o negocio…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
          className="w-64"
        />

        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="SOLICITADA">Solicitada</SelectItem>
            <SelectItem value="RECIBIDA">Recibida</SelectItem>
            <SelectItem value="EN_GESTION">En gestión</SelectItem>
            <SelectItem value="RESUELTA">Resuelta</SelectItem>
            <SelectItem value="RECHAZADA">Rechazada</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="ARREPENTIMIENTO">Arrepentimiento</SelectItem>
            <SelectItem value="BAJA_SERVICIO">Baja de servicio</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {items.length === 0 && !loading ? (
        <CrudEmptyState
          icon={ClipboardList}
          title="Sin solicitudes"
          description="Las solicitudes de arrepentimiento y baja de la suscripción del comercio con Structura aparecerán acá."
        />
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card
              key={item.id}
              className={`cursor-pointer transition-shadow hover:shadow-md ${selected?.id === item.id ? "ring-2 ring-primary" : ""}`}
              onClick={() => setSelected(selected?.id === item.id ? null : item)}
            >
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold tracking-wider">{item.referenceCode}</span>
                  <Badge className={STATUS_COLORS[item.status]}>{STATUS_LABELS[item.status]}</Badge>
                  <Badge variant="outline">{TYPE_LABELS[item.type]}</Badge>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {new Date(item.createdAt).toLocaleDateString("es-AR", {
                      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
                    })}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pt-0 text-sm">
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground">
                  <span><strong>Negocio:</strong> {item.businessName ?? "—"}</span>
                  <span><strong>Nombre:</strong> {item.requesterName}</span>
                  <span><strong>Email:</strong> {item.requesterEmail}</span>
                  {item.requesterPhone ? <span><strong>Tel:</strong> {item.requesterPhone}</span> : null}
                </div>
                {item.reason ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    <strong>Motivo:</strong> {item.reason}
                  </p>
                ) : null}

                {selected?.id === item.id ? (
                  <RequestActions item={item} onUpdated={load} />
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 ? (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Página {page + 1} de {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function RequestActions({
  item,
  onUpdated,
}: {
  item: PlatformComplianceRequest;
  onUpdated: () => void;
}) {
  const [newStatus, setNewStatus] = useState<string>("");
  const [notes, setNotes] = useState(item.resolutionNotes ?? "");
  const [saving, setSaving] = useState(false);

  const terminal = item.status === "RESUELTA" || item.status === "RECHAZADA";

  async function handleSave() {
    if (!newStatus && notes === (item.resolutionNotes ?? "")) return;
    setSaving(true);
    try {
      await apiSendJson("/api/admin/compliance-requests", "PATCH", {
        id: item.id,
        status: newStatus || item.status,
        fromStatus: item.status,
        resolutionNotes: notes || undefined,
      });
      toast.success("Solicitud actualizada");
      onUpdated();
    } catch {
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  }

  const nextStatuses = terminal ? [] : (STATUS_TRANSITIONS[item.status] ?? []);

  return (
    <div className="mt-4 space-y-3 rounded-lg border bg-muted/30 p-3" onClick={(e) => e.stopPropagation()}>
      {item.resolutionNotes && terminal ? (
        <p className="text-sm"><strong>Notas de resolución:</strong> {item.resolutionNotes}</p>
      ) : null}

      {!terminal ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium">Cambiar estado</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleccionar estado…" />
                </SelectTrigger>
                <SelectContent>
                  {nextStatuses.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s as PlatformComplianceStatus]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium">Notas de resolución</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              rows={2}
              placeholder="Agregar notas internas o resolución…"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </div>

          <Button size="sm" onClick={handleSave} disabled={saving || (!newStatus && notes === (item.resolutionNotes ?? ""))}>
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Guardar cambios
          </Button>
        </>
      ) : null}
    </div>
  );
}
